import {
  getModulePath,
  LEAGUE_MODULE_NAMES,
  overwriteRoutePaths,
} from '@leagueplatform/core';
import type { RouteObject } from 'react-router-dom';
import { ClaimsRoutes } from '@leagueplatform/claims';
import {
  HealthJourneyRoutes,
  ProgramOverviewRoutes,
} from '@leagueplatform/health-journey';
import { MemberOutreachRoutes } from '@leagueplatform/member-outreach';
import { MilestonesRoutes, GoalsRoutes } from '@leagueplatform/milestones';
import { BenefitsRoutes } from '@leagueplatform/benefits';
import {
  CaregiverRoutes,
  CaregiverClaimAccountRoutes,
} from '@leagueplatform/caregiver';
import { AuthViewLogin, AuthViewLogout } from 'pages/auth/auth.pages';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { LiveChatPage } from 'pages/live-chat/live-chat.page';
import { PreLoginLandingPage } from 'pages/pre-login-landing/pre-login-landing.page';
import { PageNotFound } from 'pages/page-not-found.page';
import { HomePage } from 'pages/home/home.page';
import { MyCaregiversPage } from 'pages/caregiver/my-caregivers.page';
import { CaregiverHomePage } from 'pages/caregiver/caregiver-home.page';
import {
  RequireMemberContainer,
  RequireCaregiver,
  RequireCaregiverClaimAuth,
} from 'components/route-guards';
import { SupportPage } from 'pages/support/support.page';
import { SetupMarketExperiencePage } from 'pages/setup-market-experience/setup-market-experience.page';
import { AuthedPreOnboardingContainer } from 'components/authed-pre-onboarding-container/authed-pre-onboarding-container.component';
import { PreOnboardingLayout } from 'components/pre-onboarding-layout/pre-onboarding-layout.component';
import { RequireMarketAndPersona } from 'components/require-market-and-persona/require-market-and-persona.component';
import { MetricsPage } from 'pages/metrics/metrics.page';
import { PersonalInfoPage } from 'pages/personal-info/personal-info.page';
import { INTERNAL_LINKS } from 'common/links';
import { AuthSessionContainer } from 'components/auth-session-container/auth-session-container';

// Define League route config override
const leagueRoutesOverride = {
  [LEAGUE_MODULE_NAMES.assessment]: '/member/health-profile/questions',
  [LEAGUE_MODULE_NAMES.healthProfile]: '/member/health-profile',
  [LEAGUE_MODULE_NAMES.healthNeedPages]: '/health-need',
  [LEAGUE_MODULE_NAMES.onboarding]: '/onboarding',
};

overwriteRoutePaths(leagueRoutesOverride);

export const appRoutes: RouteObject[] = [
  {
    path: `${APP_MANAGED_ROUTES.SIGN_IN}/*`,
    element: <AuthViewLogin />,
  },
  {
    path: `${APP_MANAGED_ROUTES.SIGN_OUT}/*`,
    element: <AuthViewLogout />,
  },
  {
    path: APP_MANAGED_ROUTES.LANDING_PAGE,
    element: <PreLoginLandingPage />,
  },
  {
    path: '/guest/:outreachCode/*',
    element: <MemberOutreachRoutes />,
  },
  // Caregiver account-claim deep link. Kept OUTSIDE AuthSessionContainer so the
  // pre-auth gate can route a signed-out caregiver to the caregiver
  // (`Caregivers`) connection instead of the SDK's default member login. Matched
  // ahead of `/caregiver/*` (below, inside AuthSessionContainer) by react-router
  // specificity ranking. Renders without member page chrome, mirroring the SDK
  // Presenter wiring.
  {
    element: <RequireCaregiverClaimAuth />,
    children: [
      {
        path: '/caregiver/claim-account/*',
        element: <CaregiverClaimAccountRoutes />,
      },
    ],
  },
  {
    element: <AuthedPreOnboardingContainer />,
    children: [
      // Caregiver-at-rest shouldn't reach the member pre-onboarding
      // flow (they have no market/persona of their own). RequireMemberContainer
      // redirects a caregiver to the caregiver home.
      {
        element: <RequireMemberContainer />,
        children: [
          {
            element: <PreOnboardingLayout />,
            children: [
              {
                path: APP_MANAGED_ROUTES.SETUP_MARKET_EXPERIENCE,
                element: <SetupMarketExperiencePage />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    element: <AuthSessionContainer />,
    children: [
      // Routes reachable by a caregiver-at-rest (NOT behind the member guard):
      // the caregiver-home shell and the caregiver management / impersonation and
      // claim-account flows.
      //
      // The caregiver-home page mounts a caregiver-only Masonry node, so it is guarded so
      // a non-caregiver member who navigates here directly is redirected home
      // instead of rendering the caregiver node in an error/empty state.
      {
        element: <RequireCaregiver />,
        children: [
          {
            path: APP_MANAGED_ROUTES.CAREGIVER_HOME,
            element: <CaregiverHomePage />,
          },
        ],
      },
      // Note: `/caregiver/claim-account/*` is handled by a top-level
      // pre-auth-gated route (see above) so a signed-out caregiver lands on the
      // caregiver connection; it is still matched ahead of `/caregiver/*` by
      // react-router specificity ranking.
      {
        path: '/caregiver/*',
        element: <CaregiverRoutes />,
      },
      // Member-only routes. A non-impersonating caregiver is redirected to the
      // caregiver home before any of these render (see
      // RequireMemberContainer). Members without a market/persona selection
      // are redirected to /setup-market-experience by RequireMarketAndPersona,
      // gated by the `release_eos_persona_selector_enabled` flag from
      // `sdk-configs` (off by default).
      {
        element: <RequireMemberContainer />,
        children: [
          {
            element: <RequireMarketAndPersona />,
            children: [
              {
                path: APP_MANAGED_ROUTES.HOME,
                element: <HomePage />,
              },
              {
                path: APP_MANAGED_ROUTES.SUPPORT,
                element: <SupportPage />,
              },
              {
                path: APP_MANAGED_ROUTES.METRICS,
                element: <MetricsPage />,
              },
              {
                path: INTERNAL_LINKS.PERSONAL_INFO,
                element: <PersonalInfoPage />,
              },
              {
                path: INTERNAL_LINKS.MY_CAREGIVERS,
                element: <MyCaregiversPage />,
              },
              ...[
                `${getModulePath(LEAGUE_MODULE_NAMES.healthJourney)}/*`,
                `${getModulePath(LEAGUE_MODULE_NAMES.healthPrograms)}/*`,
                `${getModulePath(LEAGUE_MODULE_NAMES.healthProgress)}/*`,
                `${getModulePath(LEAGUE_MODULE_NAMES.healthJourneyHistory)}/*`,
                `${getModulePath(LEAGUE_MODULE_NAMES.careLibrary)}/*`,
              ].map((path) => ({
                path,
                element: <HealthJourneyRoutes />, // Lazy loading is not used here due to Journey NodeRenderers required on the Homepage
              })),
              {
                path: `${getModulePath(LEAGUE_MODULE_NAMES.program)}/*`,
                element: <ProgramOverviewRoutes />,
              },
              {
                path: `${getModulePath(LEAGUE_MODULE_NAMES.challenge)}/*`,
                element: <ProgramOverviewRoutes />,
              },
              {
                path: `${getModulePath(LEAGUE_MODULE_NAMES.careCollections)}/*`,
                lazy: async () => ({
                  Component: (await import('@leagueplatform/care-collections'))
                    .CareCollectionsRoutes,
                }),
              },
              {
                path: `${getModulePath(LEAGUE_MODULE_NAMES.careDiscovery)}/*`,
                lazy: async () => ({
                  Component: (await import('@leagueplatform/care-discovery'))
                    .CareDiscoveryRoutes,
                }),
              },
              {
                path: `${getModulePath(LEAGUE_MODULE_NAMES.careDocuments)}/*`,
                lazy: async () => ({
                  Component: (await import('@leagueplatform/care-documents'))
                    .CareDocumentsRoutes,
                }),
              },
              {
                path: `${getModulePath(LEAGUE_MODULE_NAMES.careFinder)}/*`,
                lazy: async () => {
                  const { CareFinderRoutes, SchedulingFlowOverlay } =
                    await import('@leagueplatform/care-finder');
                  // Overlay must be in the tree wherever startSchedulingFlow fires
                  // from — the comparison surface lives under /care-finder/*.
                  return {
                    Component: () => (
                      <>
                        <SchedulingFlowOverlay />
                        <CareFinderRoutes />
                      </>
                    ),
                  };
                },
              },
              {
                path: '/appointment-scheduling/*',
                lazy: async () => ({
                  Component: (
                    await import(
                      'components/eos-appointment-scheduling-routes/eos-appointment-scheduling-routes.component'
                    )
                  ).EosAppointmentSchedulingRoutes,
                }),
              },
              {
                path: `${getModulePath(LEAGUE_MODULE_NAMES.healthNeedPages)}/*`,
                lazy: async () => ({
                  Component: (await import('@leagueplatform/care-collections'))
                    .HealthNeedPages,
                }),
              },
              {
                path: `${getModulePath(LEAGUE_MODULE_NAMES.benefits)}/*`,
                element: <BenefitsRoutes />, // Lazy loading is not used here due to Benefits NodeRenderers required on the Homepage
              },
              {
                path: `${getModulePath(LEAGUE_MODULE_NAMES.claims)}/*`,
                element: <ClaimsRoutes />, // Lazy loading is not used here due to Claims NodeRenderers required on the Homepage
              },
              {
                path: `${getModulePath(LEAGUE_MODULE_NAMES.milestones)}/*`,
                element: <MilestonesRoutes />,
              },
              {
                path: `${getModulePath(LEAGUE_MODULE_NAMES.goals)}/*`,
                element: <GoalsRoutes />,
              },
              {
                path: `${getModulePath(LEAGUE_MODULE_NAMES.healthProfile)}/*`,
                lazy: async () => ({
                  Component: (await import('@leagueplatform/health-profile'))
                    .HealthProfileRoutes,
                }),
              },
              {
                path: `${getModulePath(LEAGUE_MODULE_NAMES.aboutMe)}/*`,
                lazy: async () => ({
                  Component: (await import('@leagueplatform/about-me'))
                    .AboutMeRoutes,
                }),
              },
              {
                path: `${getModulePath(LEAGUE_MODULE_NAMES.messaging)}/*`,
                lazy: async () => ({
                  Component: (await import('@leagueplatform/messaging'))
                    .MessagingRoutes,
                }),
              },
              {
                path: `${getModulePath(LEAGUE_MODULE_NAMES.liveChat)}/*`,
                element: <LiveChatPage />,
              },
            ],
          },
        ],
      },
      {
        path: '/*',
        element: <PageNotFound />,
      },
    ],
  },
];
